import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { RecipeEditor } from '@/components/recipe/recipe-editor';
import { useForwardedTicket, useReturn, useReturnTicket } from '@/lib/return-ticket';

export const Route = createFileRoute('/recipes/$recipeId/')({
  component: RecipeEditorPage,
});

function RecipeEditorPage() {
  const { recipeId } = Route.useParams();
  const navigate = useNavigate();
  // Three different relationships to the entry this page is on, so three
  // different hooks (PLAN-LIST-STATE E3):
  //
  // - `toRecipes` goes *back* to the list this recipe was opened from, keeping
  //   the search, chips and scroll the brewer left there; a recipe opened from
  //   a shared link or a bookmark has no such entry, and pushes `/recipes` as
  //   it always did.
  // - `forwarded` moves *sideways*: a duplicate replaces this recipe rather
  //   than sitting under it, so it inherits this entry's way back (D-LS8).
  // - `ticket` goes *deeper*: the sheet and brew day return to this entry,
  //   which is still holding the list's own ticket.
  const toRecipes = useReturn(() => {
    void navigate({ to: '/recipes' });
  });
  const forwarded = useForwardedTicket();
  const ticket = useReturnTicket();

  return (
    // Keyed on the id because the router reuses this component when only the
    // param changes, and a reused page keeps everything it was holding: a
    // dialog left open, a row being edited, a scroll position. Each recipe is a
    // new mount, so none of that follows a brewer to the next one.
    //
    // It used to carry the *workspace cache* as well — a new selector answered
    // with the record before it — and that is now fixed where it belongs, in
    // `store/workspace-context.tsx`. The key stays for the local state.
    <RecipeEditor
      key={recipeId}
      recipeId={recipeId}
      onNavigate={(next) => {
        if (next === null) toRecipes();
        else
          void navigate({
            to: '/recipes/$recipeId',
            params: { recipeId: next },
            state: forwarded,
          });
      }}
      onBrewDay={(batchId) => {
        void navigate({ to: '/batches/$batchId/brew', params: { batchId }, state: ticket });
      }}
      onPlanned={(batchId) => {
        void navigate({ to: '/batches/$batchId', params: { batchId }, state: ticket });
      }}
      onBrewSheet={() => {
        void navigate({ to: '/recipes/$recipeId/sheet', params: { recipeId }, state: ticket });
      }}
    />
  );
}
